import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Paper,
  Grid,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { useState, useEffect } from "react";
import {
  Send,
  Person,
  Email,
  CheckCircle,
  Category,
} from "@mui/icons-material";
import ContactService from "../../api/services/ContactService";

const StyledCard = styled(Paper)(({ theme }) => ({
  background: "#ffffff",
  border: `1px solid ${alpha("#2a5fa9", 0.2)}`,
  borderRadius: "16px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 20px 40px ${alpha("#000", 0.1)}`,
    border: `1px solid ${alpha("#2a5fa9", 0.4)}`,
  },
  padding: "32px",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #2a5fa9, #4cbed8, #65b986, #8fc68b)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  background: "none",
  WebkitTextFillColor: "#000",
  fontWeight: 700,
  fontSize: "2.2rem",
  marginBottom: "8px",
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  color: "#65b986",
  fontWeight: 500,
  fontSize: "1.15rem",
  lineHeight: 1.6,
  marginBottom: "32px",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #2a5fa9, #65b986)",
  color: "#fff",
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "12px",
  padding: "12px 32px",
  fontSize: "1rem",
  transition: "all 0.3s ease",
  boxShadow: `0 4px 12px ${alpha("#2a5fa9", 0.3)}`,
  "&:hover": {
    background: "linear-gradient(90deg, #65b986, #2a5fa9)",
    transform: "translateY(-2px)",
    boxShadow: `0 6px 20px ${alpha("#2a5fa9", 0.4)}`,
  },
  "&:disabled": {
    background: alpha("#2a5fa9", 0.3),
    color: alpha("#fff", 0.5),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    transition: "all 0.3s ease",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#4cbed8",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2a5fa9",
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#2a5fa9",
  },
}));

const problemTypes = [
  { value: "technique", label: "Problème technique" },
  { value: "commercial", label: "Problème commercial" },
];

const Contact = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    type: "",
    societe_nom: null,
    message: "",
  });
  const [errors, setErrors] = useState({});
  const userRole = localStorage.getItem("user_role");

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      if (userData?.nom && userData?.email) {
        setForm((prev) => ({
          ...prev,
          name: userData.nom,
          email: userData.email,
          societe_nom: userData.societe || null,
        }));
      }
    } catch (error) {
      console.warn("Erreur user_data", error);
    }
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Le nom est requis";
    if (!form.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Email invalide";
    }
    if (!form.type) newErrors.type = "Sélectionnez un type de problème";
    if (!form.message.trim()) newErrors.message = "Le message est requis";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        const response = await ContactService.sendMessage(userRole, form);
        if (response.success) {
          setOpenDialog(true);
          setForm({ name: "", email: "", type: "", societe_nom: null, message: "" });
        }
      } catch (error) {
        console.error("Erreur lors de l'envoi :", error);
        alert("Une erreur est survenue lors de l'envoi du message.");
      }
    }
  };

  return (
    <Box sx={{ maxWidth: "900px", margin: "0 auto", padding: "16px" }}>
      {/* Header */}
      <Box mb={4}>
        <SectionTitle>Contactez-nous</SectionTitle>
        <SubTitle>
          Pour toute question ou demande d'assistance, notre service support est à votre écoute.
        </SubTitle>
      </Box>

      {/* Formulaire pleine largeur */}
      <StyledCard>
        <Box display="flex" flexDirection="column" gap={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Nom complet"
                variant="outlined"
                fullWidth
                name="name"
                value={form.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: "#2a5fa9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                label="Adresse e-mail"
                variant="outlined"
                fullWidth
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: "#2a5fa9" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Type de problème"
                variant="outlined"
                fullWidth
                select
                name="type"
                value={form.type}
                onChange={handleChange}
                error={!!errors.type}
                helperText={errors.type}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Category sx={{ color: "#2a5fa9" }} />
                    </InputAdornment>
                  ),
                }}
              >
                {problemTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledTextField>
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                label="Votre message"
                variant="outlined"
                fullWidth
                multiline
                rows={6}
                name="message"
                value={form.message}
                onChange={handleChange}
                error={!!errors.message}
                helperText={errors.message || `${form.message.length}/500 caractères`}
                inputProps={{ maxLength: 500 }}
                placeholder="Décrivez votre problème en détail..."
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end">
            <StyledButton startIcon={<Send />} onClick={handleSubmit} size="large">
              Envoyer le message
            </StyledButton>
          </Box>
        </Box>
      </StyledCard>

      {/* Dialog de confirmation */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{ sx: { borderRadius: "16px", padding: "8px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2, color: "#2a5fa9", fontWeight: 600 }}>
          <CheckCircle sx={{ color: "#65b986", fontSize: "32px" }} />
          Message envoyé avec succès
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: alpha("#000", 0.7) }}>
            Merci de nous avoir contactés.
          </Typography>
          <Typography sx={{ color: alpha("#000", 0.7), mt: 2 }}>
            Notre équipe vous répondra dans les plus brefs délais.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <StyledButton onClick={() => setOpenDialog(false)}>Fermer</StyledButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contact;